/**
 * React wrapper for the imperative WebGL View3D in the room editor.
 *
 * View3D owns the canvas rendering and the label layer (appended to the stage
 * container); React treats those as opaque. The view comes from the shell's
 * camera controls (`useCreateView3D`), which keep its drag tool and render
 * params in step with the view widget; the room is pushed by effect. Picks
 * and the move gadget's drags read the editor state through a ref, so the
 * latest state is always used. The gadget's drags land in the editor's
 * gesture engine (beginFreeMove3D / moveFree3D / endDrag), so they draft,
 * snap, commit, and undo like a plan drag.
 */

import { css } from '@emotion/react';
import {
  beginFreeMove3D,
  type EditorMutableState,
  endDrag,
  moveFree3D,
  type Selection,
  showsElement,
  useFloorPlanEditorState,
} from '@kaiizen/kai-floor-plan-editor-core';
import { useEffect, useRef } from 'react';
import { useCreateView3D, type View3D } from '../view3d';
import { ViewCube } from './ViewCube';

const VIEW3D_CANVAS_STYLE = css`
  label: VIEW3D_CANVAS_STYLE;
  position: absolute; inset: 0; width: 100%; height: 100%;
`;

/**
 * Show the room as it stands, the draft during a drag. The view menu applies
 * to both views: hidden elements are left out of the 3D scene the way the 2D
 * plan leaves them out.
 */
const showRoom = (view: View3D, st: EditorMutableState): void => {
  const { layers } = st;
  view.setShape(st.draftShape ?? st.shape, st.selection, !st.readOnly, { showElement: element => showsElement(layers, element) });
  view.show();
};

export function View3DCanvas({ visible }: { visible: boolean }): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  const stateRef = useRef(floorPlanEditorState);
  stateRef.current = floorPlanEditorState;
  const { canvasRef, viewRef } = useCreateView3D<Selection>(visible, {
    onSelect: selection => stateRef.current.select(selection ?? { kind: 'none' }),
    onGadget: {
      begin: () => {
        const { selection } = stateRef.current.st;
        if (selection.kind === 'inner') stateRef.current.mutate(s => beginFreeMove3D(s, selection.index));
      },
      move: (delta, reach) => stateRef.current.mutate(s => moveFree3D(s, delta, reach)),
      end: () => stateRef.current.mutate(s => endDrag(s)),
    },
  });
  const { st } = floorPlanEditorState;

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (visible) showRoom(view, st);
    else view.hide();
  }, [visible, st, viewRef]);

  return (
    <>
      <canvas ref={canvasRef} css={VIEW3D_CANVAS_STYLE} data-testid='view3d' style={{ display: visible ? 'block' : 'none' }} />
      {visible && <ViewCube />}
    </>
  );
}
